import React, { useState, useEffect } from "react";
import { Modal, Tabs, Upload, Button, Input, Divider, message } from "antd";
import imageCompression from "browser-image-compression";
import "../../../styles/bulletinAdmin/components/bulletinModal.scss";
import { UploadOutlined, LinkOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;

export default function UploadModal({
  type = "file",
  visible,
  setVisible,
  onUploadFinish,
}) {
  const [activeTab, setActiveTab] = useState("upload");
  const [embedLink, setEmbedLink] = useState("");

  const shouldAllowEmbed = type === "video" || type === "image";
  const shouldAllowUpload = type === "image" || type === "file";

  const closeModal = () => {
    setVisible(false);
    setEmbedLink("");
    setActiveTab(shouldAllowEmbed && !shouldAllowUpload ? "embed" : "upload");
  };

  useEffect(() => {
    if (visible) {
      setActiveTab(!shouldAllowUpload && shouldAllowEmbed ? "embed" : "upload");
    }
  }, [visible, type, shouldAllowEmbed, shouldAllowUpload]);

  const handleEmbed = () => {
    if (!embedLink.trim()) return;
    onUploadFinish?.(embedLink.trim());
    closeModal();
  };

  const handleUpload = async (file, onSuccess) => {
    let fileToUpload = file;

    // Only compress if the file is an image
    if (file.type.startsWith("image/")) {
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      try {
        message.loading({
          content: "Compressing image...",
          key: "compressing",
        });
        fileToUpload = await imageCompression(file, options);
        message.success({
          content: "Image compressed!",
          key: "compressing",
          duration: 2,
        });
      } catch (error) {
        console.error("Image compression error:", error);
        message.error("Could not process image. Please try another one.");
        onSuccess("fail");
        return;
      }
    }

    const formData = new FormData();
    // Use the (potentially compressed) file for the upload
    formData.append("file", fileToUpload, file.name); // Keep original filename

    const token = localStorage.getItem("api_token");
    fetch(`${process.env.REACT_APP_API_URL}/ebulletin/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then(async (res) => {
        const contentType = res.headers.get("content-type");
        if (!res.ok) throw new Error("HTTP error " + res.status);
        if (contentType && contentType.includes("application/json")) {
          return res.json();
        } else {
          throw new Error("Not JSON: " + (await res.text()));
        }
      })
      .then((data) => {
        onUploadFinish?.({
          name: data.name,
          type: file.type,
          url: data.url,
        });
        onSuccess("ok");
        closeModal();
      })
      .catch((error) => {
        console.error("Upload error:", error);
        message.error(`Upload failed: ${error.message}`);
        onSuccess("fail");
      });
  };

  const getAcceptType = () => {
    switch (type) {
      case "image":
        return "image/*";
      case "video":
        return "video/*";
      case "file":
        return ".pdf";
      default:
        return "*";
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={closeModal}
      footer={null}
      width={300}
      className="upload-modal"
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {shouldAllowUpload && (
          <TabPane tab="Upload" key="upload">
            <Upload
              accept={getAcceptType()}
              showUploadList={false}
              customRequest={({ file, onSuccess }) =>
                handleUpload(file, onSuccess)
              }
            >
              <Button type="primary" icon={<UploadOutlined />} block>
                Choose {type === "image" ? "an" : "a"} {type}
              </Button>
            </Upload>
          </TabPane>
        )}

        {shouldAllowEmbed && (
          <TabPane tab="Embed link" key="embed">
            <Input
              placeholder={`Paste a ${type} link...`}
              prefix={<LinkOutlined />}
              className="mt-2"
              value={embedLink}
              onChange={(e) => setEmbedLink(e.target.value)}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              block
              onClick={handleEmbed}
            >
              Embed {type}
            </Button>
            {type === "video" && (
              <div>
                <Divider />
                <p className="text-muted">
                  Supported platforms: YouTube, Google Drive
                </p>
              </div>
            )}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
}
